import { useState, useCallback } from 'react';
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import type { User, UserFormData, CurrentUser, BulkUploadResults } from '../types';
import * as userService from '../services/userService';

interface UseUsersReturn {
  users: User[];
  loading: boolean;
  showModal: boolean;
  editingUser: User | null;
  showBulkUploadResults: boolean;
  bulkUploadResults: BulkUploadResults | null;
  error: string;
  loadUsers: () => Promise<User[]>;
  handleAdd: (data: UserFormData, currentUser: CurrentUser) => Promise<void>;
  handleEdit: (data: UserFormData, currentUser: CurrentUser) => Promise<void>;
  handleDelete: (userId: string) => Promise<void>;
  handleBulkUpload: (file: File, currentUser: CurrentUser) => Promise<void>;
  getFilteredUsers: (currentUser: CurrentUser) => User[];
  openModal: (user?: User) => void;
  closeModal: () => void;
  closeBulkUploadResults: () => void;
  refreshUsers: () => Promise<void>;
}

export const useUsers = (): UseUsersReturn => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [showBulkUploadResults, setShowBulkUploadResults] = useState(false);
  const [bulkUploadResults, setBulkUploadResults] = useState<BulkUploadResults | null>(null);
  const [error, setError] = useState('');

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const data = await userService.getUsers();
      setUsers(data);
      return data;
    } catch (err: any) {
      setError(err.message || 'Failed to load users.');
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  const refreshUsers = useCallback(async () => {
    const data = await userService.getUsers();
    setUsers(data);
  }, []);

  const handleAdd = async (data: UserFormData, currentUser: CurrentUser) => {
    let userData: UserFormData;

    if (currentUser.role === 'super_admin') {
      userData = { ...data, role: 'admin' as const, universityId: data.universityId };
    } else if (currentUser.role === 'admin') {
      const { password, ...rest } = data;
      userData = { ...rest, role: 'user' as const, universityId: currentUser.universityId! };
    } else {
      throw new Error('Invalid user role for creating users');
    }

    const addedUser = await userService.addUser(userData);
    setUsers(prev => [...prev, addedUser]);
  };

  const handleEdit = async (data: UserFormData, currentUser: CurrentUser) => {
    if (!editingUser) throw new Error('No user selected for editing');

    if (currentUser.role === 'super_admin' && editingUser.role === 'admin') {
      if (data.role !== 'admin') {
        throw new Error('Super admin cannot change university admin role to regular user.');
      }
      if (!data.universityId) {
        throw new Error('University admin must be assigned to a university.');
      }
    }

    await userService.updateUser(editingUser.id, data);
    setUsers(prev =>
      prev.map(user =>
        user.id === editingUser.id
          ? { ...user, ...data, universityId: currentUser.role === 'admin' ? currentUser.universityId! : data.universityId }
          : user
      )
    );
  };

  const handleDelete = async (userId: string) => {
    await userService.deleteUser(userId);
    setUsers(prev => prev.filter(user => user.id !== userId));
  };

  const getFilteredUsers = useCallback((currentUser: CurrentUser): User[] => {
    if (currentUser.role === 'super_admin') {
      return users.filter(user => user.role === 'admin');
    } else if (currentUser.role === 'admin') {
      return users.filter(
        user => user.role === 'user' && user.universityId === currentUser.universityId
      );
    }
    return [];
  }, [users]);

  const handleBulkUpload = async (file: File, currentUser: CurrentUser) => {
    const results: BulkUploadResults = { success: [], errors: [], skipped: [] };

    const processData = (data: Record<string, any>[]) => {
      data.forEach((row, index) => {
        const { name, email, status } = row;

        if (!name || !email) {
          results.errors.push({ row: index + 1, reason: 'Missing required fields (name or email)', data: row });
          return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          results.errors.push({ row: index + 1, reason: 'Invalid email format', data: row });
          return;
        }

        if (status && !['active', 'inactive'].includes(status.toLowerCase())) {
          results.errors.push({ row: index + 1, reason: 'Invalid status (must be "active" or "inactive")', data: row });
          return;
        }

        const existingUser = users.find(u => u.email.toLowerCase() === email.toLowerCase());
        if (existingUser) {
          results.skipped.push({ row: index + 1, reason: 'User already exists', data: row });
          return;
        }

        results.success.push({
          row: index + 1,
          data: {
            name: name.trim(),
            email: email.trim().toLowerCase(),
            status: status ? status.toLowerCase() : 'active',
            role: 'user',
            universityId: currentUser.role === 'admin' ? currentUser.universityId : null,
          },
        });
      });
    };

    const addUsersToDb = async () => {
      for (const successItem of [...results.success]) {
        try {
          await userService.addUser(successItem.data as UserFormData);
        } catch (err: any) {
          results.errors.push({ row: successItem.row, reason: `Database error: ${err.message}`, data: successItem.data });
          results.success = results.success.filter(item => item.row !== successItem.row);
        }
      }
      await refreshUsers();
      setBulkUploadResults(results);
      setShowBulkUploadResults(true);
    };

    if (file.name.endsWith('.csv')) {
      return new Promise<void>((resolve, reject) => {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: async (parseResults) => {
            try {
              processData(parseResults.data as Record<string, any>[]);
              await addUsersToDb();
              resolve();
            } catch (err) {
              reject(err);
            }
          },
          error: (err) => reject(new Error(`CSV parsing error: ${err.message}`)),
        });
      });
    } else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
      return new Promise<void>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
          try {
            if (e.target?.result && e.target.result instanceof ArrayBuffer) {
              const data = new Uint8Array(e.target.result);
              const workbook = XLSX.read(data, { type: 'array' });
              const sheetName = workbook.SheetNames[0];
              const worksheet = workbook.Sheets[sheetName];
              const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 }) as any[][];

              const headers = jsonData[0] as string[];
              const rows = jsonData.slice(1);
              const dataObjects = rows.map((row) => {
                const obj: Record<string, any> = {};
                headers.forEach((header, idx) => {
                  obj[header] = row[idx];
                });
                return obj;
              });

              processData(dataObjects);
              await addUsersToDb();
              resolve();
            } else {
              reject(new Error('Failed to read file'));
            }
          } catch (err) {
            reject(err);
          }
        };
        reader.onerror = () => reject(new Error('Failed to read file'));
        reader.readAsArrayBuffer(file);
      });
    } else {
      throw new Error('Unsupported file format. Please use CSV or Excel files.');
    }
  };

  const openModal = (user?: User) => {
    setEditingUser(user || null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingUser(null);
  };

  const closeBulkUploadResults = () => {
    setShowBulkUploadResults(false);
    setBulkUploadResults(null);
  };

  return {
    users,
    loading,
    showModal,
    editingUser,
    showBulkUploadResults,
    bulkUploadResults,
    error,
    loadUsers,
    handleAdd,
    handleEdit,
    handleDelete,
    handleBulkUpload,
    getFilteredUsers,
    openModal,
    closeModal,
    closeBulkUploadResults,
    refreshUsers,
  };
};
